import React, { useState, useEffect } from 'react';
import { getCategories } from "./categoryService";
import "./JobsManagement.css";
import { getJobs } from "./jobService";
import * as XLSX from "xlsx";
import {
  getCampaigns,
  assignJobsToCampaign,
  getRequests
} from "../services/jobManagementService.js";

function JobsManagement({ onLogout }) {
  // Category & Campaign State
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');

  // Requests Data & Streaming
  const [requests, setRequests] = useState([]);
  const [campaignByJobId, setCampaignByJobId] = useState({});
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [isBackgroundFetching, setIsBackgroundFetching] = useState(false);
  const [fetchedPagesCount, setFetchedPagesCount] = useState(0);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');

  // Selection & Pagination
  const [checkedRequestIds, setCheckedRequestIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [requestsPerPage, setRequestsPerPage] = useState(100);

  // Status
  const [isCompiling, setIsCompiling] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // 1. Fetch Categories & Campaigns on Mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [catData, campData] = await Promise.all([
          getCategories(),
          getCampaigns()
        ]);

        const loadedCategories = catData.data || catData;
        const sortedCategories = [...loadedCategories].sort((a, b) =>
          (a.name || '').trim().localeCompare((b.name || '').trim(), undefined, { sensitivity: "base" })
        );
        setCategories(sortedCategories);

        const loadedCampaigns = campData.data || campData;
        setCampaigns(loadedCampaigns);
      } catch (error) {
        console.error("Failed to fetch categories or campaigns:", error);
      }
    };

    loadInitialData();
  }, []);

  // 2. Fetch Jobs only after a category is selected
  useEffect(() => {
    let isSubscribed = true;

    const loadJobs = async () => {
      if (!selectedCategory) {
        setRequests([]);
        setCheckedRequestIds([]);
        setLoadingRequests(false);
        setIsBackgroundFetching(false);
        setFetchedPagesCount(0);
        setCampaignByJobId({});
        return;
      }

      try {
        setLoadingRequests(true);
        setIsBackgroundFetching(true);
        setFetchedPagesCount(0);
        setRequests([]);
        setCampaignByJobId({});

        // Fetch local database requests for campaign mapping
        const campaignResponse = await getRequests();

        const campaignMap = {};
        if (Array.isArray(campaignResponse)) {
          campaignResponse.forEach((request) => {
            if (Array.isArray(request)) {
              const jobId = request[0];
              const campaignName = request[5];
              if (jobId) campaignMap[String(jobId)] = campaignName || "";
            } else if (request && typeof request === 'object') {
              const jobId = request.id;
              if (jobId) campaignMap[String(jobId)] = request.campaign_name || "";
            }
          });
        }

        if (isSubscribed) {
          setCampaignByJobId(campaignMap);
        }

        // Get actual jobs from TechZeus
        await getJobs(
          selectedCategory,
          (accumulatedJobs, pageNum, isLastPage) => {
            if (!isSubscribed) return;

            setRequests(accumulatedJobs);
            setFetchedPagesCount(pageNum);

            if (pageNum === 1) setLoadingRequests(false);
            if (isLastPage) setIsBackgroundFetching(false);
          },
          "all"
        );
      } catch (err) {
        console.error("Error fetching jobs/campaign information:", err);
        if (isSubscribed) {
          setRequests([]);
          setCampaignByJobId({});
        }
      } finally {
        if (isSubscribed) {
          setLoadingRequests(false);
          setIsBackgroundFetching(false);
        }
      }
    };

    loadJobs();

    return () => {
      isSubscribed = false;
    };
  }, [selectedCategory]);

  // Handle Search Input Change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Filter Requests by Search Query
  const filteredRequests = requests.filter((req) => {
    if (searchQuery.trim() === '') return true;
    const query = searchQuery.trim().toLowerCase();

    const matchId = String(req.id).toLowerCase().includes(query);
    const matchSnap = req.snapJobId || req.snap_job_id ? String(req.snapJobId || req.snap_job_id).toLowerCase().includes(query) : false;
    const matchTitle = req.title ? req.title.toLowerCase().includes(query) : false;

    return matchId || matchSnap || matchTitle;
  });

  // Dynamic Pagination Calculations
  const totalPages = Math.max(1, Math.ceil(filteredRequests.length / requestsPerPage));
  const startIndex = (currentPage - 1) * requestsPerPage;
  const endIndex = startIndex + requestsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    const validPage = Math.max(1, Math.min(newPage, totalPages));
    setCurrentPage(validPage);
    document.querySelector(".scrollable-jobs-panel")?.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  // Checkbox Selection Handlers
  const toggleCheckbox = (id) => {
    setCheckedRequestIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    setCheckedRequestIds(paginatedRequests.map((req) => req.id));
  };

  const handleClearAll = () => {
    setCheckedRequestIds([]);
  };

  // Assign selected jobs
  const handleAssignJobs = async () => {
    if (checkedRequestIds.length === 0) {
      setSuccessMessage('');
      alert("Please select at least one job.");
      return;
    }

    if (!selectedCampaign) {
      setSuccessMessage('');
      alert("Please select a Campaign.");
      return;
    }

    setIsCompiling(true);
    setSuccessMessage('');

    const assignedJobIds = [...checkedRequestIds];

    try {
      const result = await assignJobsToCampaign({
        campaign_id: parseInt(selectedCampaign, 10),
        request_ids: assignedJobIds,
      });

      if (result.success) {
        const selectedCampaignData = campaigns.find(
          (camp) => String(camp.id) === String(selectedCampaign)
        );

        const campaignName =
          selectedCampaignData?.title ||
          selectedCampaignData?.name ||
          `Campaign #${selectedCampaign}`;

        setCampaignByJobId((prev) => {
          const updated = { ...prev };
          assignedJobIds.forEach((jobId) => {
            updated[String(jobId)] = campaignName;
          });
          return updated;
        });

        setSuccessMessage(
          result.message ||
          `${assignedJobIds.length} job(s) assigned successfully to campaign.`
        );

        setCheckedRequestIds([]);

        setTimeout(() => {
          setSuccessMessage('');
        }, 4000);

      } else {
        alert(result.message || "Failed to assign jobs.");
      }

    } catch (error) {
      console.error("Job assignment error:", error);
      alert("Network or server error while assigning jobs.");
    } finally {
      setIsCompiling(false);
    }
  };

  const selectedCount = checkedRequestIds.length;

  // Excel Download
  const handleDownloadExcel = () => {
    if (paginatedRequests.length === 0) {
      alert("No jobs available to download.");
      return;
    }

    const excelData = paginatedRequests.map((req) => ({
      "Job ID": req.id,
      "Snap Job ID": req.snapJobId || req.snap_job_id || "N/A",
      "Job Title": req.title || "Untitled",
      "Status": req.status || "N/A",
      "Campaign": campaignByJobId[String(req.id)] || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Jobs");
    XLSX.writeFile(workbook, `Jobs_Management_Page_${currentPage}.xlsx`);
  };

  return (
    <div className="dashboard-canvas">
      {/* Top Header */}
      <header className="dashboard-header">
        <h1>JOBS MANAGEMENT</h1>
      </header>

      {/* Top Controls: Job Category & Campaign */}
      <div className="top-controls-row">
        <div className="input-group">
          <label>JOB CATEGORY</label>
          <select 
            value={selectedCategory} 
            onChange={(e) => {
              setSelectedCategory(e.target.value);
              setCheckedRequestIds([]);
              setCurrentPage(1);
            }}
          >
            <option value="">Select Category</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.attributes?.name || category.name || `Category #${category.id}`}
              </option>
            ))}
          </select>
        </div>

        <div className="input-group">
          <label>CAMPAIGN</label>
          <select 
            value={selectedCampaign} 
            onChange={(e) => {
              setSelectedCampaign(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">Select Campaign</option>
            {campaigns.map((camp) => (
              <option key={camp.id} value={camp.id}>
                {camp.title || camp.name || `Campaign #${camp.id}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Requests Matrix Section */}
      <div className="middle-settings-split">
        <div className="content-column job-matrix-column">
          <div className="section-header">
            <div className="section-header-title-group">
              <span className="section-label">SELECT JOBS</span>

              {/* Instant Search Bar */}
              <input
                type="text"
                placeholder="Search Request ID / Snap Job ID / Job Title"
                value={searchQuery}
                onChange={handleSearchChange}
                className="search-job-input"
              />

              {/* Fetch Loader */}
              {(isBackgroundFetching || loadingRequests) && (
                <div className="job-fetch-spinner">
                  <div className="spinner-icon" />
                  <span className="fetch-text">
                    Fetching Page {fetchedPagesCount}... ({requests.length} jobs)
                  </span>
                </div>
              )}
            </div>

            <div className="selection-actions">
              <span className="job-counter">
                Selected: {selectedCount} | Showing {filteredRequests.length === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} jobs
              </span>

              <select
                value={requestsPerPage}
                onChange={(e) => {
                  setRequestsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                  setCheckedRequestIds([]);
                }}
                className="jobs-per-page-select"
              >
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
                <option value={200}>200 / page</option>
              </select>

              <button type="button" className="btn-select-all" onClick={handleSelectAll}>
                Select All
              </button>

              <button type="button" className="btn-clear-all" onClick={handleClearAll}>
                Clear
              </button>

              <button
                type="button"
                className="btn-download-excel"
                onClick={handleDownloadExcel}
              >
                Excel
              </button>
            </div>
          </div>

          {/* Table Data View */}
          <div className="scrollable-jobs-panel">
            {loadingRequests ? (
              <div className="loading-jobs-spinner">
                <div className="spinner-icon"></div>
                <span>Fetching job records...</span>
              </div>
            ) : paginatedRequests.length === 0 ? (
              <div className="empty-job-message">
                No job records match the selected category.
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th className="checkbox-cell">
                      <input
                        type="checkbox"
                        checked={paginatedRequests.length > 0 && paginatedRequests.every(r => checkedRequestIds.includes(r.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            handleSelectAll();
                          } else {
                            handleClearAll();
                          }
                        }}
                      />
                    </th>
                    <th>Job ID</th>
                    <th>Snap Job ID</th>
                    <th>Job Title</th>
                    <th>Status</th>
                    <th>Campaign</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRequests.map((req) => (
                    <tr 
                      key={req.id} 
                      onClick={() => toggleCheckbox(req.id)}
                    >
                      <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={checkedRequestIds.includes(req.id)}
                          onChange={() => toggleCheckbox(req.id)}
                        />
                      </td>
                      <td>{req.id}</td>
                      <td>{req.snapJobId || req.snap_job_id || 'N/A'}</td>
                      <td>{req.title || 'Untitled'}</td>
                      <td>{req.status || 'N/A'}</td>
                      <td>{campaignByJobId[String(req.id)] || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination Controls */}
          <div className="pagination-controls">
            <div className="nav-btn-group">
              <button
                type="button"
                className="btn-page-step"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(1)}
              >
                &laquo;
              </button>
              <button
                type="button"
                className="btn-page-step"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                &lt;
              </button>
            </div>

            <div className="page-jump-container">
              <span>Page</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={currentPage}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (val >= 1 && val <= totalPages) handlePageChange(val);
                }}
                className="page-number-input"
              />
              <span>of {totalPages}</span>
            </div>

            <div className="nav-btn-group">
              <button
                type="button"
                className="btn-page-step"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                &gt;
              </button>
              <button
                type="button"
                className="btn-page-step"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(totalPages)}
              >
                &raquo;
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <footer className="bottom-actions-deck">
        {successMessage && (
          <div className="success-banner">
            {successMessage}
          </div>
        )}

        <button
          className="btn-generate-report"
          onClick={handleAssignJobs}
          disabled={isCompiling}
        >
          {isCompiling ? 'Assigning Jobs...' : 'ASSIGN JOBS'}
        </button>
      </footer>
    </div>
  );
}

export default JobsManagement;